import React from 'react';
import PivotTableSummary from './PivotTableSummary';
import { PIVOT_TABLE_CONTENT_ID } from './PivotTableSummary';
import { PivotTableData, ServiceAttributeSummaryData } from '../../models/PulseData';
import PulseContentHeader from './PulseContentHeader';
import PulseContentFooter from './PulseContentFooter';
import { copyToClipboard, copyImageToClipboard } from '../../utils/clipboardUtils';
import { useNotifier } from '../../hooks/useNotifier';
import { toBlob } from 'html-to-image';
import logger from '../../utils/logger';
import { openPrintView } from './printUtils';
import { SummaryType, NotifierType } from './PulseNav'; // Assuming NotifierType is exported or defined
import { PivotViewRenderPayload } from './PulseContent'; // Import the specific payload type

interface PivotViewProps extends PivotViewRenderPayload {
  servicesGroup: string;
  currentStartDate: Date;
  currentEndDate: Date;
  mainCssUrl: string;
  // showNotification: (message: string, type: NotifierType) => void; // Option 1: Pass down
  isLoadingOverall: boolean;
  activeSummary: SummaryType;
}

const PivotView: React.FC<PivotViewProps> = ({
  data, // from PivotViewRenderPayload
  servicesGroup,
  currentStartDate,
  currentEndDate,
  mainCssUrl,
  isLoadingOverall,
  // activeSummary // Not directly used in this example for title, but available
}) => {
  const { showNotification } = useNotifier(); // Option 2: Use hook directly

  const handleCopyRawData = async () => {
    const payload = getPivotRawDataPayload(data);
    if (payload) {
      await copyToClipboard(JSON.stringify(payload.data, null, 2), showNotification, payload.message);
    } else {
      showNotification('No raw data available for pivot view.', 'warning');
    }
  };

  const handleCopyView = async () => {
    const viewCopyInfo = getPivotViewCopyInfo();
    const elementToCapture = document.getElementById(viewCopyInfo.elementId);
    if (elementToCapture) {
      try {
        const imageBlob = await toBlob(elementToCapture, { backgroundColor: 'white' });
        if (imageBlob) await copyImageToClipboard(imageBlob, showNotification, viewCopyInfo.successMessage);
        else throw new Error('Failed to generate blob.');
      } catch (err) {
        logger.error('Error capturing pivot view as image:', err);
        showNotification('Failed to copy pivot view as image.', 'error');
      }
    } else {
      showNotification(`Element '${viewCopyInfo.elementId}' not found.`, 'warning');
    }
  };

  const handlePrintView = async () => {
    const printInfo = getPivotPrintInfo(servicesGroup);
    const elementToCapture = document.getElementById(printInfo.elementId);
    if (elementToCapture) {
      openPrintView({ elementToCapture, jobTitle: printInfo.jobTitle, mainCssUrl, headerContent: printInfo.headerContent, showNotification });
    } else {
      showNotification('Could not find pivot content to print.', 'warning');
    }
  };

  const title = `Pivot Table of ERRORs: ${servicesGroup && servicesGroup !== "All" ? servicesGroup : 'ALL'}`;

  return (
    <>
      <PulseContentHeader
        title={title}
        isLoading={isLoadingOverall}
        onCopyRawData={handleCopyRawData}
        onCopyView={handleCopyView}
        onPrintView={handlePrintView}
      />
      {/* PivotTableSummary already includes a div with PIVOT_TABLE_CONTENT_ID */}
      <PivotTableSummary data={data} />
      <PulseContentFooter currentStartDate={currentStartDate} currentEndDate={currentEndDate} />
    </>
  );
};

export const getPivotRawDataPayload = (pivotTableData: PivotTableData): { data: any; message: string } | null => {
  if (!pivotTableData) return null;
  return {
    data: pivotTableData,
    message: 'Pivot table data copied to clipboard!',
  };
};

export const getPivotViewCopyInfo = () => {
  return {
    elementId: PIVOT_TABLE_CONTENT_ID,
    successMessage: 'Pivot table view copied to clipboard!',
  };
};

export const getPivotPrintInfo = (servicesGroup: string) => {
  return {
    elementId: PIVOT_TABLE_CONTENT_ID,
    jobTitle: `Errors Table - ${servicesGroup && servicesGroup !== "All" ? servicesGroup : 'ALL'} - ${new Date().toLocaleDateString()}`,
    headerContent: `Errors Table for ${servicesGroup && servicesGroup !== "All" ? servicesGroup : 'ALL'}`,
  };
};
export default PivotView;